/**
 * Modal "Descripción para post" — el texto para Instagram que se escribe a partir de
 * una imagen — ADR-2026-09-16-los-menus-del-visor-son-componentes.
 *
 * Vivía embebido en el visor del historial (versión desktop); salió acá cuando el
 * lienzo del Agente necesitó EL MISMO modal sin el visor. Presentacional: el host
 * pide la descripción y trae el texto; acá van la espera (con su barra de 20 s), el
 * texto y Copiar. Los clicks no burbujean: adentro del lightbox, uno cerraba el visor.
 */
import { useEffect, useState } from 'react';
import { Check, Copy, Loader2, X } from 'lucide-react';

export interface DescripcionParaPostModalProps {
  open: boolean;
  generando: boolean;
  texto: string;
  copiado: boolean;
  onCopiar: () => void;
  onClose: () => void;
}

export const DescripcionParaPostModal: React.FC<DescripcionParaPostModalProps> = ({
  open,
  generando,
  texto,
  copiado,
  onCopiar,
  onClose,
}) => {
  // La barra avanza (no lineal, 20 s) desde el frame siguiente a abrir con la espera.
  const [progreso, setProgreso] = useState(false);
  useEffect(() => {
    if (open && generando) {
      setProgreso(false);
      const raf = requestAnimationFrame(() => setProgreso(true));
      return () => cancelAnimationFrame(raf);
    }
    setProgreso(false);
  }, [open, generando]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-[2px] flex items-center justify-center p-4 z-[10000]"
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
    >
      <div
        className="bg-surface-card rounded-lg p-[var(--card-padding-mobile)] md:p-[var(--card-padding-desktop)] max-w-md w-full shadow-lg border border-neutral-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-[length:var(--text-section-title-mobile)] md:text-[length:var(--text-section-title-desktop)] font-semibold text-neutral-900">
            {generando ? 'Generando descripción' : 'Descripción para post'}
          </h3>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onClose();
            }}
            className="w-8 h-8 inline-flex items-center justify-center rounded-md text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100 transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        {generando ? (
          <div className="mb-6">
            <div className="flex items-center gap-3 text-neutral-600 mb-4">
              <Loader2 className="w-5 h-5 animate-spin text-brand-primary shrink-0" />
              <span className="text-[length:var(--text-body)]">Generando descripción para post de Instagram...</span>
            </div>
            <div className="h-1.5 rounded-full overflow-hidden bg-neutral-100">
              <div
                className="h-full bg-brand-primary rounded-full"
                style={{ width: progreso ? '100%' : '0%', transition: 'width 20s cubic-bezier(0, 0, 0.2, 1)' }}
              />
            </div>
          </div>
        ) : (
          <>
            <div className="text-[length:var(--text-body)] text-neutral-600 whitespace-pre-wrap mb-6 max-h-[50vh] overflow-y-auto">
              {texto}
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={onCopiar}
                className={`h-[var(--btn-h)] px-5 inline-flex items-center justify-center gap-2 ${
                  copiado ? 'bg-success hover:bg-success' : 'bg-brand-primary hover:bg-brand-primary-hover'
                } text-white rounded-md font-medium text-[length:var(--text-body)] shadow-sm transition-colors`}
              >
                {copiado ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copiado ? 'Copiado' : 'Copiar'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DescripcionParaPostModal;
